import { Button } from '@codegouvfr/react-dsfr/Button';
import { useTextes } from '@common/config';
import { useDeferredAction, useSessionContext } from '@common/hooks';
import {
  EtablissementProjection,
  useDeleteOrganismeEtablissementMutation,
  useGetOrganismeEtablissementsQuery,
} from '@common/services';
import { ConfirmationModal, FcPaginator, getModalById } from '@common/ui';
import { EtablissementDeclarantConsolidesCard } from '@common/ui/etablissement';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {useAppDispatch, useAppSelector} from '../../../../hooks/store.hook';
import {
  etablissementModified,
  needNewDeclaration, resetEtablissementModified
} from '../../../Dashboard/components/VerticalStepper/stores/dashboard-stepper.slice';
import { useEtablissementsDeclarantsConsolides } from './hooks/use-etablissements-declarants-consolides.hook';

const deleteEtablissementConfirmationModal = getModalById('etablissement-delete-confirmation-modal');

const DEFAULT_SORTERS = [
  { field: 'principal', direction: 'desc' },
  { field: 'typeDeclaration', direction: 'asc' },
  { field: 'denominationUsuelle', direction: 'asc' },
];

export const EditOfaEtablissementsList = () => {
  const t = useTextes('ParcoursOfa', 'EtapierStep2');
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { organismeId } = useSessionContext();

  const [page, setPage] = useState<number>(0);
  const [confirmationModalDescription, setConfirmationModalDescription] = useState<string>('');

  const { prepareAction, performAction, clearAction } = useDeferredAction();

  const hasModifiedEtablissements = useAppSelector((state) => state.dashboardStepperSlice.hasModifiedEtablissements);

  const { data: pageEtablissementCards, isLoading } = useGetOrganismeEtablissementsQuery({
    id: organismeId,
    projection: EtablissementProjection.CARD,
    page,
    size: 5,
    sorters: DEFAULT_SORTERS,
  }, { refetchOnMountOrArgChange: true });

  // Permet de retourner à la page précédente dans le cas où on supprime tous les établissements de la page actuelle
  useEffect(() => {
    if (pageEtablissementCards?.content.length === 0 && pageEtablissementCards?.pageable.pageNumber > 0) {
      setPage(page - 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageEtablissementCards]);

  const etablissementsDeclarantsConsolides = useEtablissementsDeclarantsConsolides(pageEtablissementCards);

  const [deleteOrganismeEtablissement] = useDeleteOrganismeEtablissementMutation();

  const handleDelete = (etablissementId: number, denominationUsuelle: string) => {
    setConfirmationModalDescription(`Vous êtes sur le point de supprimer l'établissement ${denominationUsuelle}.`);
    prepareAction(() => deleteOrganismeEtablissement({ id: organismeId, etablissementId }).unwrap());
    deleteEtablissementConfirmationModal.open();
  };

  const handleFinish = () => {
    if (hasModifiedEtablissements) {
      dispatch(needNewDeclaration());
    }
    dispatch(resetEtablissementModified());
    navigate('/tableau-de-bord');
  };

  const handleConfirmationDeletion = async () => {
    await performAction();
    dispatch(etablissementModified());
    deleteEtablissementConfirmationModal.close();
  };

  if (isLoading) return null;

  return (
    <div className={'flex flex-col gap-4 items-center p-4 pb-8'}>

      <div className={'flex flex-col w-full max-w-[1200px]'}>

        <div className={'flex items-center'}>
          <h3 className={'flex-1 !mb-1'}>{t['ETAB_OFA_PRIN_TEXTE1']}</h3>
        </div>

        <span className={'mt-4'}>{t['ETAB_OFA_PRIN_TEXTE2']}</span>
      </div>

      <div className={'w-full flex flex-col max-w-[1200px] py-[56px] px-[102px] bg-white gap-8'}>

        <h6 style={{ margin: 0 }} className={'pb-4 border-b-1 border-[#DDDDDD]'}>Récapitulatif des établissements</h6>

        {etablissementsDeclarantsConsolides.map(({ declarant, consolides }, index) => (
          <div key={index}>
            <EtablissementDeclarantConsolidesCard declarant={declarant}
                                                  consolides={consolides}
                                                  onEdit={(etablissementId) => navigate(`/mon-ofa/edition/etablissement/modifier/${etablissementId}`)}
                                                  onDelete={(etablissementId, denominationUsuelle) => handleDelete(etablissementId, denominationUsuelle)} />
          </div>
        ))}

        {(pageEtablissementCards?.totalPages > 1) && (
          <div className={'flex flex-col items-center'}>
            <FcPaginator page={pageEtablissementCards?.pageable.pageNumber ?? 0}
                         totalPages={pageEtablissementCards?.totalPages ?? 0}
                         onPageChange={setPage} />
          </div>
        )}

        <Button iconId={'fr-icon-add-line'}
                style={{ width: '100%', textAlign: 'center', justifyContent: 'center' }}
                priority={'secondary'}
                onClick={() => navigate(`/mon-ofa/edition/etablissement/ajout`)}>
          AJOUTER UN ÉTABLISSEMENT
        </Button>

        <div className={'flex justify-end gap-2'}>
          <Button onClick={handleFinish}>VALIDER</Button>
        </div>
      </div>

      <ConfirmationModal dsfrModal={deleteEtablissementConfirmationModal}
                         description={confirmationModalDescription}
                         subDescription="Êtes-vous certain(e) de vouloir continuer ?"
                         cancelProps={{ onClick: clearAction }}
                         confirmProps={{ onClick: handleConfirmationDeletion }} />
    </div>
  );
};